import React, { useState, useContext, useEffect, useMemo, useCallback } from "react";
import { makeStyles } from "@material-ui/styles";
import { AppContext, DispatchContext } from "../../AppContext";
import * as actions from "../../actions/actions";
import Service from "../../services/service";
import TitleBar from '../../components/titleBar';
import LoadingScreen from '../../components/LoadingScreen.js';
import Pagination from '../../components/pagination';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import Table from '../../components/table';
import Checkbox from '@material-ui/core/Checkbox';
import { config, timeConverter, getModuloColor, resolveNotificationMessage } from '../../helpers/constants';
import Paper from '@material-ui/core/Paper';
import IconButton from '@material-ui/core/IconButton';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import PlayCircleIcon from '@material-ui/icons/PlayCircleFilled';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import LibraryAddIcon from '@material-ui/icons/LibraryAdd';
import ReportDetails from '../../components/ReportDetails';
import PauseIcon from '@material-ui/icons/PauseCircleOutline';
import { CircularProgress, Typography, Link } from '@material-ui/core';
import ResultOverview from "../../components/ResultOverview";
import NewProjectForm from '../../components/NewProjectForm';
import IconLabel from '../../components/IconLabel';

const useStyles = makeStyles((theme) => ({
    root: {
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
    },
    title: {
        flex: "0 0 auto",
        height: 0,
    },
    body_container: {
        display: 'flex',
        flex: '1',
        flexDirection: 'row',
        position: "relative",
    },
    content: {
        position: "relative",
        flex: "1 1 auto",
        overflow: "hidden",
        overflowY: "auto",    // FIXME, showing temp scroll bar during loading in table vview
        padding: 0,
        height: 0,  // no clue why this is required, but omitting won't display a vertical scroll bar
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        alignContent: "flex-start",
    },
    bottom: {
        display: 'flex',
        alignItems: 'center',
        marginTop: 'auto',
        padding: '10px 10px 10px 15px',
        height: '43px',
        borderTop: "1px solid #dddddd",
    },
    item: {
        width: "33%",
        [theme.breakpoints.down('md')]: {
            width: "50%",
        },
        padding: theme.spacing(1),
        height: "150px"
    },
    pagination: {
        marginLeft: 'auto'
    },
    resultWidth: {
        width: '150px',
        backgroundColor: '#455641'
    },
    paper: {
        display: "flex",
        flex: "1 1 0",
        minHeight: 0,
        margin: 10,
        boxShadow: "0px 0px 2px 2px rgba(0, 0, 0, 0.1)",
        zIndex: 2,
        flexDirection: 'column',
        position: "relative"
    },
    paperwithOutBackground: {
        background: 'transparent',
        boxShadow: 'none',
        margin: 0,
        display: "flex",
        flex: "1 1 0",
        minHeight: 0,
        zIndex: 2,
        flexDirection: 'column',
        position: "relative"
    },
    checkIcon: {
        color: 'green'
    },
    clockIcon: {
        color: 'black'
    },
    cacheIcon: {
        color: 'grey'
    },
    errorBox: {
        color: 'red'
    },
    table_preview_header: {
        width: '65px',
        textAlign: "center",
    },
    table_result_header: {
        width: '65px',
        textAlign: "center",
    },
    table_action_header: {
        width: '90px',
        textAlign: 'center'

    },
    table_title_header: {
        width: "60%",
        minWidth: "100px"
    },
    table_project_header: {
        width: '40%',
        minWidth: "100px",
    },
    projectTag: {
        backgroundColor: getModuloColor(1),
        borderRadius: 3,
        paddingLeft: 5,
        paddingRight: 5,
        flex: "0 0 auto"
    },
    table_credits_header: {
        width: '60px',
        textAlign: "center",
        cursor: "default",
    },
    table_status_header: {
        width: '50px',
        textAlign: "center",
    },
    table_date_header: {
        width: '100px',
        cursor: "default",
    },
    reportContainerSize: {
        backgroundColor: '#ffffff',
        width: 350,
        minWidth: 350
    },
    overlay: {
        zIndex: "99",
        right: "0px",
        top: "0",
        left: "0",
        bottom: "0",
        display: "flex",
        justifyContent: "center",
        position: "absolute",
        backgroundColor: "rgba(0,0,0,0.6)",
    },
    helper_container: {
        left: 0,
        right: 0,
        bottom: 0,
        display: "flex",
        zIndex: -1,
        opacity: 1,
        transition: "opacity 225ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
        position: "fixed",
        alignItems: "center",
        touchAction: "none",
        justifyContent: 'center',
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        webkitTapHighlightColor: "transparent",
    },
    reportStatusContainer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        opacity: 1,
        transition: "opacity 225ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
        color: '#FFFFFF'
    },
    centerAlign: {
        textAlign: "center"
    },
    resultReportPercent: {
        fontSize: "12px",
        fontWeight: "800",
        display: "block",
        marginTop: "-21px",
    },
    marginFix: {
        marginBottom: "-3px"
    }
}))

export default function ReportOverViewComponent(props) {
    const state = useContext(AppContext);
    const s = state.strings;
    const dispatch = useContext(DispatchContext);
    const classes = useStyles(state);
    const [reportsLoading, setReportsLoading] = useState(false);
    let intervalGetReportSource;
    const timestamp = Math.floor(Date.now() / 1000 / 10);   // new timestamp all 10 secs to prevent caching of overview bars

    useEffect(() => {
        loadReports();
        dispatch(actions.setUI({ plagCheckMenuExpanded: true }));
        if (window && window.location && state.ui && state.ui.pageName !== window.location.pathname) {
            dispatch(actions.navigate_to(window.location.pathname))
        }
        return () => {
            clearInterval(intervalGetReportSource);
        };
    }, [state.reportsChangeNo]);

    function loadReports() {
        setReportsLoading(true);
        Service.getReports({ filters: state.reportFilter, ...state.reportPagination, pagesize: state.user.settings.pagesize }).then(res => {
            setReportsLoading(false);
            if (res.success) {
                dispatch(actions.get_reports(res || []))
            } else {
                dispatch(actions.openToast({ type: "error", message: res.errors[0], status: true }));
            }
        });

        if (state.activeReportId) {
            Service.getReport(state.activeReportId).then(res => {
                dispatch(actions.get_report(res.data || []));
            });
        }
    }

    const onSelectId = (reportID, e) => {
        dispatch(actions.navigate_to("/plagiarism/reports/" + reportID));
        dispatch(actions.change_report_card(reportID));
    }

    const onChangePage = (page, pageLength) => {
        dispatch(actions.change_pagination({
            ...state.reportPagination,
            pagesize: pageLength,
            pageno: page
        }));
    }

    const columns = useMemo(() => [
        {
            Header: ({ getToggleAllRowsSelectedProps }) => <Checkbox size="small" {...getToggleAllRowsSelectedProps()} />,
            accessor: "Active",
            Cell: ({ row }) => <Checkbox size="small" onClick={(e) => e.stopPropagation()} {...row.getToggleRowSelectedProps()} />,
            headerClassName: classes.table_status_header,
            cellClassName: classes.table_status_header,
            disableSortBy: true,
        },
        {
            Header: s.status,
            accessor: "Status",
            headerClassName: classes.table_status_header,
            cellClassName: classes.table_status_header,
            Cell: ({ cell: { value } }) => <>
                {value === 'Ok' ? <span className={classes.checkIcon}> <CheckCircleOutlineIcon /> </span> :
                    value === 'Geplant' ? <span className={classes.clockIcon}> <AccessTimeIcon /> </span> :
                        value === 'Fehler' ? <span className={classes.errorBox}> <ErrorOutlineIcon /> </span> :
                            value === 'Pausiert' ? <span className={classes.cacheIcon}> <PauseIcon /> </span> :
                                value === 'Aktiv' ? <span className={classes.cacheIcon}> <CircularProgress size={20} /> </span> : ''
                }
            </>,
        },
        {
            Header: s.date,
            accessor: "Scheduled",
            Cell: ({ cell: { value } }) => <>{timeConverter(value, true, s.common.dateFormat)}</>,
            headerClassName: classes.table_date_header,
            cellClassName: classes.table_date_header,
        },
        {
            Header: s.title,
            accessor: "Name",
            cellClassName: classes.table_title_header,
            headerClassName: classes.table_title_header,
            Cell: ({ cell: { value, row } }) => <Link style={{ cursor: "pointer" }} onClick={() => onSelectId(row.original.Id)}>{value}</Link>
        },
        {
            Header: s.project,
            accessor: "ProjectName",
            headerClassName: classes.table_project_header,
            cellClassName: classes.table_project_header,
            Cell: ({ cell: { value, row } }) => <div style={{ display: "flex" }}><div style={{ backgroundColor: getModuloColor(row.original.ProjectId) }} className={classes.projectTag}>{value}</div></div>,
        },
        {
            Header: s.result,
            accessor: "ResultPercent",
            headerClassName: classes.table_result_header,
            cellClassName: classes.table_result_header,
            Cell: ({ cell: { value, row } }) => <ResultOverview value={value} navigateOnClick={false} />
        },
        {
            Header: s.preview,
            accessor: "Id",
            headerClassName: classes.table_preview_header,
            cellClassName: classes.table_preview_header,
            disableSortBy: true,
            Cell: ({ cell: { value } }) => <img className={classes.marginFix} src={`${config.backendUrl}button?reportid=${value}&size=0&time=${timestamp}`} alt="preview" />
        },
        {
            Header: s.pages,
            accessor: "Pages",
            headerClassName: classes.table_credits_header,
            cellClassName: classes.table_credits_header,
        },
        {
            Header: s.credits,
            accessor: "Credits",
            headerClassName: classes.table_credits_header,
            cellClassName: classes.table_credits_header,
        },
        {
            Header: s.actions,
            accessor: "Actions",
            headerClassName: classes.table_action_header,
            cellClassName: classes.table_action_header,
            disableSortBy: true,
            Cell: ({ cell: { value, row } }) => <div> <IconButton size="small" disabled={(row.original.Status !== "Ok")} onClick={(e) => onDownloadReport(e, row.original)}><CloudDownloadIcon /></IconButton> <IconButton size="small" onClick={(e) => onDeleteReport(e, row.original)} disabled={(row.original.Status !== "Ok" && row.original.Status !== "Fehler" && row.original.Status !== "Geplant" && row.original.Status !== "Pausiert")}> <DeleteForeverIcon /></IconButton> </div>,
        },
    ], [state.lang]
    );

    const onDownloadReport = (e, data) => {
        e && e.stopPropagation();
        dispatch(actions.openReportDownloadDialog([data.PermaLinkId]));
    }

    const onDeleteReport = (e, row) => {
        e && e.stopPropagation();
        dispatch(actions.openConfirmationDialog({
            title: s.reportOverview.deletePlagCheck,
            text: s.reportOverview.deletePlagCheckDesc,
            onConfirm: () => deleteReports([row.Id])
        }));
    }

    const deleteMultipleRow = () => {
        dispatch(actions.openConfirmationDialog({
            title: s.reportOverview.deletePlagChecks.format(state.selectedReportIds.length),
            text: s.reportOverview.deletePlagChecksDesc,
            hint: s.reportOverview.deletePlagChecksHint,
            onConfirm: () => deleteReports(state.selectedReportIds)
        }));
    }

    const addToLibraryButtonClicked = () => {
        dispatch(actions.openConfirmationDialog({
            title: s.reportOverview.addToLibraryTitle.format(state.selectedReportIds.length),
            text: s.reportOverview.addToLibraryDesc,
            hint: s.reportOverview.addToLibraryHint,
            onConfirm: () => addTextsToLibrary(state.selectedReportIds),
            waitingMessage: s.reportOverview.addingTexts
        }));
    }

    const addTextsToLibrary = (reportids) => {
        Service.AddTextsToLibrary({ reportids: reportids }).then(res => {
            if (res.success) {
                let message = s.notificationMessage.addedToLibrary;
                let type = 'success';
                if (res.message === 'notificationMessage.textsAdded') {
                    type = 'success';
                    message = s.notificationMessage.textsAdded.format(res.total);
                } else if (res.message === 'notificationMessage.textsSkipped') {
                    type = 'warning';
                    message = s.notificationMessage.textsSkipped.format(res.total);
                }
                dispatch(actions.closeConfirmationDialog());
                dispatch(actions.changeReportSelection([]));
                dispatch(actions.setActiveReport(null));    
                dispatch(actions.openToast({ type: type, message: message, status: true }));
            } else {
                dispatch(actions.openToast({ type: "error", message: resolveNotificationMessage(res.errors[0], s.notificationMessage) }));
            }
        });
    }



    const deleteReports = (reportids) => {
        Service.deleteReports(reportids).then(res => {
            if (res && res.success) {
                dispatch(actions.changeReportSelection([]));
                dispatch(actions.setActiveReport(null));    // make sure to reset active report just in case a report to be deleted is currently active
                dispatch(actions.notifyReportsChanged());
                dispatch(actions.openToast({ type: "success", message: resolveNotificationMessage(res.message, s.notificationMessage).format(res.total), status: true }));
            } else {
                dispatch(actions.openToast({ type: "error", message: resolveNotificationMessage(res.errors[0], s.notificationMessage), status: true }));
            }
        });
    }

    const activateReports = () => {
        Service.activateReports(state.selectedReportIds).then(res => {
            if (res && res.success) {
//                dispatch(actions.changeReportSelection([]));
  //              dispatch(actions.setActiveReport(null));    // make sure to reset active report just in case a report to be deleted is currently active
                dispatch(actions.notifyReportsChanged());
                dispatch(actions.openToast({ type: "success", message: resolveNotificationMessage(res.message, s.notificationMessage).format(res.total), status: true }));
            } else {
                dispatch(actions.openToast({ type: "error", message: resolveNotificationMessage(res.errors[0], s.notificationMessage), status: true }));
            }
        });
    }


    // handle active row changed
    const onActiveRowHandle = async (rowDetails, index) => {
        if (rowDetails) {
            dispatch(actions.setActiveReport(rowDetails.Id));       // immediate feedback to table 
            Service.getReport(rowDetails.Id).then(res => {    // load details for new active report, this updates the details panel
                dispatch(actions.get_report(res.data || []));
            });
        } else {
            dispatch(actions.setActiveReport(null));
            dispatch(actions.setNewCheckSettings({ revisionReportId: null }));
        }
    }

    const rowSelectedHandle = (rows) => {
        dispatch(actions.changeReportSelection(rows.map(r => r.Id)) || []);
    }

    const handleSort = (sortBy) => {
        const sortByDetails = sortBy[0] || {
            id: 'Date',
            desc: true
        }

        const reportFilter = {
            ...state.reportPagination,
            sortby: sortByDetails.id,
            sortdir: sortByDetails.desc === true ? 'desc' : 'asc'
        }
        dispatch(actions.change_pagination(reportFilter));
    }

    // get informed when the report was reset (usually by ReportDetails)
    const onResetReport = useCallback(() => {
        dispatch(actions.notifyReportsChanged());
    });

    return (
        <div className={classes.root}>
            {state.openCreateProjectPopup && <NewProjectForm />}
            <TitleBar className={classes.title} title={s.performedPlagiairismChecks} type="ReportOverview" />
            <div className={classes.body_container}>
                <Paper elevation={3} className={state.performCheckLayout === 'list' ? classes.paper : classes.paperwithOutBackground}>
                    <LoadingScreen status={reportsLoading} />
                    <div className={classes.content}>
                        {(state.reports.length > 0) ?
                            <Table
                                data={state.reports}
                                onSelectedRowsChange={rowSelectedHandle}
                                selectedIds={state.selectedReportIds}
                                columns={columns}
                                onActiveRowChanged={onActiveRowHandle}
                                activeRowId={state.activeReportId}
                                onSort={handleSort}
                                onSortBy={state.reportPagination}

                            />
                            : (!reportsLoading && <IconLabel centered label={s.reportOverview.noReportsFound} spacing={32} />)}
                    </div>

                    <div className={classes.bottom}>

                        {state.selectedReportIds.length > 0 &&
                            <div style={{ display: "flex", alignItems: "center" }}>
                                <Typography variant="body2">{s.reportOverview.selectedReports.format(state.selectedReportIds.length)}</Typography>
                                <IconButton data-for="globalTT" data-tip={s.reportOverview.activateReportsTip} style={{ marginLeft: 8 }} size="small" onClick={activateReports}> <PlayCircleIcon /></IconButton>
                                <IconButton data-for="globalTT" data-tip={s.reportOverview.addSelectedToLibrary} style={{ marginLeft: 8 }} size="small" onClick={addToLibraryButtonClicked}> <LibraryAddIcon /></IconButton>
                                <IconButton data-for="globalTT" data-tip={s.reportOverview.deleteReportsTip} style={{ marginLeft: 8 }} size="small" onClick={deleteMultipleRow}> <DeleteForeverIcon /></IconButton>
                            </div>
                        }

                        <div className={classes.pagination}>
                            <Pagination
                                page={state.reportPagination.pageno}
                                pageLength={state.user.settings.pagesize}
                                totalRecords={state.totalReportsNumber || 0}
                                onchange={onChangePage}
                            />
                        </div>
                    </div>
                </Paper>
                {state.ui.propertyBarVisible &&
                    <div className={classes.reportContainerSize}  >
                        <ReportDetails view={true} onResetReport={onResetReport} />
                    </div>
                }
            </div>
        </div >
    )
}